import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, VideoOff, Mic, MicOff, Radio, Users, Send, X, 
  Languages, Settings, Share2, Heart, MessageCircle, Eye,
  Volume2, VolumeX, Maximize2, Minimize2, Gift, Crown, Diamond
} from 'lucide-react';
import SimplePeer from 'simple-peer';
import axios from 'axios';
import { GiftPanel, GiftButton, LiveStreamStats, useGiftSystem, GIFTS } from './GiftSystem';
import { usePremiumGiftEffects } from './PremiumGiftEffects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Support 20+ languages for live translation
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', flag: '🇧🇩' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
  { code: 'fil', name: 'Filipino', flag: '🇵🇭' },
  { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
  { code: 'cs', name: 'Czech', flag: '🇨🇿' }
];

// Stream Categories
const STREAM_CATEGORIES = [
  { id: 'gaming', name: 'Gaming', icon: '🎮', color: '#8B5CF6' },
  { id: 'music', name: 'Music', icon: '🎵', color: '#EC4899' },
  { id: 'just_chatting', name: 'Just Chatting', icon: '💬', color: '#3B82F6' },
  { id: 'sports', name: 'Sports', icon: '⚽', color: '#10B981' },
  { id: 'creative', name: 'Creative', icon: '🎨', color: '#F59E0B' },
  { id: 'education', name: 'Education', icon: '📚', color: '#6366F1' },
  { id: 'cooking', name: 'Cooking', icon: '🍳', color: '#EF4444' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: '#14B8A6' },
  { id: 'fitness', name: 'Fitness', icon: '💪', color: '#F97316' },
  { id: 'technology', name: 'Technology', icon: '💻', color: '#8B5CF6' },
  { id: 'news', name: 'News & Politics', icon: '📰', color: '#64748B' },
  { id: 'entertainment', name: 'Entertainment', icon: '🎬', color: '#A855F7' }
];

const LiveStream = ({ user, ws, onClose }) => {
  const [mode, setMode] = useState(null); // 'streamer', 'viewer', 'discover', 'schedule', 'earnings'
  const [streamId, setStreamId] = useState('');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [translationEnabled, setTranslationEnabled] = useState(true);
  const [streamTitle, setStreamTitle] = useState('');
  const [streamerLanguage, setStreamerLanguage] = useState('en');
  const [streamInfo, setStreamInfo] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  
  // New feature states
  const [selectedCategory, setSelectedCategory] = useState('just_chatting');
  const [filterCategory, setFilterCategory] = useState('');
  const [followedStreams, setFollowedStreams] = useState([]);
  const [scheduledStreams, setScheduledStreams] = useState([]);
  const [upcomingStreams, setUpcomingStreams] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  const [recentTips, setRecentTips] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [activeTab, setActiveTab] = useState('live'); // 'live', 'following', 'scheduled'
  
  // Schedule stream states
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleCategory, setScheduleCategory] = useState('just_chatting');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDescription, setScheduleDescription] = useState('');
  
  // Gift system hooks
  const [userDiamonds, setUserDiamonds] = useState(100);
  const giftSystem = useGiftSystem(streamId, user?.id);
  const { activeEffect, effectSender, triggerEffect, clearEffect, PremiumGiftEffects } = usePremiumGiftEffects();
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const chatContainerRef = useRef(null);
  const peerRef = useRef(null);
  const streamWsRef = useRef(null);

  // Fetch user diamonds on mount
  useEffect(() => {
    const fetchDiamonds = async () => {
      try {
        const response = await axios.get(`${API}/users/me/diamonds`);
        setUserDiamonds(response.data.diamonds || 100);
      } catch (error) {
        console.error('Error fetching diamonds:', error);
      }
    };
    if (user) fetchDiamonds();
  }, [user]);

  // Handle sending gift
  const handleSendGift = async (gift, quantity) => {
    if (!streamInfo?.streamer_id) return;
    
    try {
      const response = await axios.post(`${API}/gifts/send`, {
        gift_id: gift.id,
        gift_name: gift.name,
        diamonds: gift.diamonds,
        quantity,
        stream_id: streamId,
        recipient_id: streamInfo.streamer_id
      });
      
      if (response.data.success) {
        setUserDiamonds(response.data.remaining_diamonds);
        giftSystem.addGift(gift, { id: user?.id, name: user?.name, avatar: user?.avatar }, quantity);
        setShowGiftModal(false);
      }
    } catch (error) {
      console.error('Error sending gift:', error);
    }
  };

  // Handle premium gift effect
  const handlePremiumGift = (giftId, sender) => {
    triggerEffect(giftId, sender || { name: user?.name });
  };

  // Connect to stream-specific WebSocket
  useEffect(() => {
    if (mode && streamId) {
      connectStreamWebSocket();
    }
    return () => {
      if (streamWsRef.current) {
        streamWsRef.current.close();
      }
    };
  }, [mode, streamId]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const connectStreamWebSocket = () => {
    const streamWs = new WebSocket(`${WS_URL}/api/ws/${user.id}`);
    
    streamWs.onopen = () => {
      console.log('Stream WebSocket connected');
      // Join stream room
      streamWs.send(JSON.stringify({
        type: 'stream_join',
        stream_id: streamId,
        user_id: user.id,
        user_name: user.name,
        is_streamer: mode === 'streamer'
      }));
    };

    streamWs.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      handleStreamMessage(data);
    };

    streamWsRef.current = streamWs;
  };

  const handleStreamMessage = async (data) => {
    switch (data.type) {
      case 'stream_chat':
        // Translate message if needed
        let messageToAdd = data;
        if (translationEnabled && data.original_language !== selectedLanguage) {
          try {
            const translated = await translateText(data.message, selectedLanguage);
            messageToAdd = { ...data, translated_message: translated };
          } catch (e) {
            console.error('Translation error:', e);
          }
        }
        setChatMessages(prev => [...prev, messageToAdd]);
        break;
      
      case 'stream_viewer_count':
        setViewerCount(data.count);
        break;
      
      case 'stream_reaction':
        addFloatingReaction(data.emoji);
        break;
      
      case 'video_signal':
        handleVideoSignal(data);
        break;
      
      case 'stream_info':
        setStreamInfo(data);
        setStreamerLanguage(data.language || 'en');
        break;
      
      case 'stream_ended':
        if (mode === 'viewer') {
          alert('Stream has ended');
          handleLeaveStream();
        }
        break;
    }
  };

  const handleVideoSignal = (data) => {
    if (data.signal_type === 'offer' && mode === 'viewer') {
      createAnswer(data.data, data.from_user_id);
    } else if (data.signal_type === 'answer' && mode === 'streamer') {
      peerRef.current?.signal(data.data);
    } else if (data.signal_type === 'ice-candidate') {
      peerRef.current?.signal(data.data);
    }
  };

  const translateText = async (text, targetLang) => {
    try {
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: LANGUAGES.find(l => l.code === targetLang)?.name || targetLang
      });
      return response.data.translated_text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  };

  const addFloatingReaction = (emoji) => {
    const id = Date.now() + Math.random();
    setReactions(prev => [...prev, { id, emoji, x: Math.random() * 80 + 10 }]);
    setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== id));
    }, 3000);
  };

  const startStreaming = async () => {
    if (!streamTitle.trim()) {
      alert('Please enter a stream title');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true
      });
      
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      const newStreamId = `stream_${user.id}_${Date.now()}`;
      setStreamId(newStreamId);
      setMode('streamer');
      setIsLive(true);

      // Create peer for broadcasting
      const peer = new SimplePeer({
        initiator: true,
        trickle: false,
        stream
      });

      peer.on('signal', (signal) => {
        streamWsRef.current?.send(JSON.stringify({
          type: 'video_signal',
          stream_id: newStreamId,
          signal_type: 'offer',
          data: signal
        }));
      });

      peerRef.current = peer;

      // Notify server about stream start
      streamWsRef.current?.send(JSON.stringify({
        type: 'stream_start',
        stream_id: newStreamId,
        title: streamTitle,
        streamer_id: user.id,
        streamer_name: user.name,
        language: streamerLanguage,
        category: selectedCategory
      }));

    } catch (error) {
      console.error('Error starting stream:', error);
      alert('Please allow camera and microphone access');
    }
  };

  const joinStream = async (targetStreamId) => {
    setStreamId(targetStreamId);
    setMode('viewer');
    
    // Request stream info
    streamWsRef.current?.send(JSON.stringify({
      type: 'stream_join_request',
      stream_id: targetStreamId,
      viewer_id: user.id,
      viewer_name: user.name
    }));
  };

  const fetchLiveStreams = async () => {
    setLoadingStreams(true);
    try {
      const url = filterCategory 
        ? `${API}/streams/live?category=${filterCategory}`
        : `${API}/streams/live`;
      const response = await axios.get(url);
      setLiveStreams(response.data || []);
    } catch (error) {
      console.error('Error fetching live streams:', error);
      setLiveStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  const fetchFollowedStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams/following`);
      setFollowedStreams(response.data || []);
    } catch (error) {
      console.error('Error fetching followed streams:', error);
    }
  };

  const fetchUpcomingStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams/schedule/upcoming`);
      setUpcomingStreams(response.data || []);
    } catch (error) {
      console.error('Error fetching upcoming streams:', error);
    }
  };

  const fetchMyScheduledStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams/schedule/my`);
      setScheduledStreams(response.data || []);
    } catch (error) {
      console.error('Error fetching scheduled streams:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/streams/earnings/my`);
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const handleFollowStreamer = async (streamerId) => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/streams/follow/${streamerId}`);
        setIsFollowing(false);
        setFollowerCount(prev => prev - 1);
      } else {
        await axios.post(`${API}/streams/follow/${streamerId}`);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error following/unfollowing:', error);
    }
  };

  const handleSendTip = async () => {
    if (!tipAmount || parseFloat(tipAmount) < 1) {
      alert('Minimum tip is $1');
      return;
    }
    
    try {
      await axios.post(`${API}/streams/${streamId}/tip`, null, {
        params: {
          amount: parseFloat(tipAmount),
          message: tipMessage
        }
      });
      setShowTipModal(false);
      setTipAmount('');
      setTipMessage('');
    } catch (error) {
      console.error('Error sending tip:', error);
      alert(error.response?.data?.detail || 'Error sending tip');
    }
  };

  const handleScheduleStream = async () => {
    if (!scheduleTitle || !scheduleDate || !scheduleTime) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const scheduledTime = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      await axios.post(`${API}/streams/schedule`, null, {
        params: {
          title: scheduleTitle,
          category: scheduleCategory,
          scheduled_time: scheduledTime,
          description: scheduleDescription
        }
      });
      
      setScheduleTitle('');
      setScheduleDate('');
      setScheduleTime('');
      setScheduleDescription('');
      fetchMyScheduledStreams();
      alert('Stream scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling stream:', error);
      alert(error.response?.data?.detail || 'Error scheduling stream');
    }
  };

  const handleCancelScheduledStream = async (scheduleId) => {
    try {
      await axios.delete(`${API}/streams/schedule/${scheduleId}`);
      fetchMyScheduledStreams();
    } catch (error) {
      console.error('Error cancelling stream:', error);
    }
  };

  const handleDiscoverClick = () => {
    setMode('discover');
    fetchLiveStreams();
    fetchFollowedStreams();
    fetchUpcomingStreams();
  };

  const handleJoinFromDiscover = (stream) => {
    setStreamId(stream.id);
    setStreamInfo(stream);
    setIsFollowing(stream.is_following || false);
    setMode('viewer');
  };

  const createAnswer = (offer, fromUserId) => {
    const peer = new SimplePeer({
      initiator: false,
      trickle: false
    });

    peer.on('signal', (signal) => {
      streamWsRef.current?.send(JSON.stringify({
        type: 'video_signal',
        stream_id: streamId,
        signal_type: 'answer',
        data: signal,
        target_user_id: fromUserId
      }));
    });

    peer.on('stream', (stream) => {
      setRemoteStream(stream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    });

    peer.signal(offer);
    peerRef.current = peer;
  };

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageData = {
      type: 'stream_chat',
      stream_id: streamId,
      user_id: user.id,
      user_name: user.name,
      message: newMessage,
      original_language: selectedLanguage,
      timestamp: new Date().toISOString()
    };

    streamWsRef.current?.send(JSON.stringify(messageData));
    setNewMessage('');
  };

  const sendReaction = (emoji) => {
    streamWsRef.current?.send(JSON.stringify({
      type: 'stream_reaction',
      stream_id: streamId,
      user_id: user.id,
      emoji
    }));
    addFloatingReaction(emoji);
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  const handleLeaveStream = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (peerRef.current) {
      peerRef.current.destroy();
    }
    streamWsRef.current?.send(JSON.stringify({
      type: mode === 'streamer' ? 'stream_end' : 'stream_leave',
      stream_id: streamId,
      user_id: user.id
    }));
    onClose();
  };

  // Mode Selection Screen
  if (!mode) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center overflow-y-auto py-8"
      >
        <div className="text-center max-w-4xl mx-auto px-4">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <div className="mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Radio className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pulsate Global Live
            </h2>
            <p className="text-white/60">Stream or watch live with real-time translation</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto mb-6">
            {/* Start Streaming */}
            <div className="p-4 sm:p-6 rounded-3xl bg-gradient-to-br from-red-500/20 to-pink-600/20 border border-white/10">
              <Video className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Go Live</h3>
              <p className="text-white/60 text-sm mb-4">Start broadcasting to your audience</p>
              
              <div className="space-y-3 mb-4">
                <input
                  data-testid="stream-title-input"
                  type="text"
                  value={streamTitle}
                  onChange={(e) => setStreamTitle(e.target.value)}
                  placeholder="Stream title..."
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-red-400 text-sm"
                />
                <select
                  data-testid="stream-category-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-red-400 text-sm"
                >
                  {STREAM_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id} className="bg-gray-900">
                      {cat.name}
                    </option>
                  ))}
                </select>
                <select
                  data-testid="streamer-language-select"
                  value={streamerLanguage}
                  onChange={(e) => setStreamerLanguage(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-red-400 text-sm"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code} className="bg-gray-900">
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                data-testid="start-streaming-button"
                onClick={startStreaming}
                className="w-full py-3 rounded-xl font-semibold text-white shadow-lg hover:scale-105 active:scale-95 transition-transform"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #EC4899 100%)' }}
              >
                Start Streaming
              </button>
            </div>

            {/* Discover Streams */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-teal-600/20 border border-white/10">
              <Users className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Discover</h3>
              <p className="text-white/60 text-sm mb-4">Browse active live streams</p>
              
              <div className="space-y-3 mb-4">
                <select
                  data-testid="viewer-language-select"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-emerald-400 text-sm"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code} className="bg-gray-900">
                      {lang.flag} {lang.name} (Translate)
                    </option>
                  ))}
                </select>
                <div className="h-[48px] flex items-center justify-center">
                  <p className="text-white/40 text-xs">Find streamers from around the world</p>
                </div>
              </div>

              <button
                data-testid="discover-streams-button"
                onClick={handleDiscoverClick}
                className="w-full py-3 rounded-xl font-semibold text-white shadow-lg hover:scale-105 active:scale-95 transition-transform"
                style={{ background: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)' }}
              >
                Browse Streams
              </button>
            </div>

            {/* Schedule Stream */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-white/10">
              <svg className="w-12 h-12 text-amber-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl font-bold text-white mb-2">Schedule</h3>
              <p className="text-white/60 text-sm mb-4">Plan your future streams</p>
              
              <div className="h-[106px] flex items-center justify-center">
                <p className="text-white/40 text-xs text-center">Notify followers when you go live</p>
              </div>

              <button
                data-testid="schedule-stream-button"
                onClick={() => { setMode('schedule'); fetchMyScheduledStreams(); }}
                className="w-full py-3 rounded-xl font-semibold text-white shadow-lg hover:scale-105 active:scale-95 transition-transform"
                style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)' }}
              >
                Schedule Stream
              </button>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto mb-6">
            {/* Watch by ID */}
            <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-600/20 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-lg font-bold text-white">Watch Live</h3>
                  <p className="text-white/60 text-xs">Join with stream ID</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <input
                  data-testid="join-stream-id-input"
                  type="text"
                  value={streamId}
                  onChange={(e) => setStreamId(e.target.value)}
                  placeholder="Enter stream ID..."
                  className="flex-1 px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-purple-400 text-sm"
                />
                <button
                  data-testid="join-stream-button"
                  onClick={() => joinStream(streamId)}
                  disabled={!streamId}
                  className="px-4 py-2 rounded-xl font-semibold text-white disabled:opacity-50"
                  style={{ background: '#8B5CF6' }}
                >
                  Join
                </button>
              </div>
            </div>

            {/* Earnings Dashboard */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-lg font-bold text-white">Earnings</h3>
                  <p className="text-white/60 text-xs">View tips & revenue</p>
                </div>
              </div>
              
              <button
                data-testid="earnings-button"
                onClick={() => { setMode('earnings'); fetchEarnings(); }}
                className="w-full py-2 rounded-xl font-semibold text-white"
                style={{ background: '#10B981' }}
              >
                View Earnings
              </button>
            </div>
          </div>

          {/* Language Support Info */}
          <div className="p-4 rounded-2xl bg-white/5 max-w-3xl mx-auto">
            <p className="text-white/60 text-sm flex items-center justify-center gap-2">
              <Languages className="w-4 h-4" />
              Supporting {LANGUAGES.length}+ languages with real-time translation
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Discover Streams Screen
  if (mode === 'discover') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
        data-testid="discover-streams-container"
      >
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMode(null)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-white">Discover Live Streams</h2>
                <p className="text-white/60 text-sm">Find streamers from around the world</p>
              </div>
            </div>
            <button
              data-testid="refresh-streams-button"
              onClick={fetchLiveStreams}
              className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 flex items-center gap-2"
            >
              <svg className={`w-4 h-4 ${loadingStreams ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'live', label: 'All Live', count: liveStreams.length },
              { id: 'following', label: 'Following', count: followedStreams.length },
              { id: 'scheduled', label: 'Upcoming', count: upcomingStreams.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {tab.label} {tab.count > 0 && `(${tab.count})`}
              </button>
            ))}
          </div>

          {/* Category Filter */}
          <div className="mb-6">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <button
                onClick={() => { setFilterCategory(''); fetchLiveStreams(); }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  !filterCategory 
                    ? 'bg-white text-black' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                All Categories
              </button>
              {STREAM_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { setFilterCategory(cat.id); fetchLiveStreams(); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                    filterCategory === cat.id 
                      ? 'text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                  style={filterCategory === cat.id ? { background: cat.color } : {}}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Language Filter */}
          <div className="mb-6">
            <label className="text-white/60 text-sm mb-2 block">Translate chat to:</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white outline-none"
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code} className="bg-gray-900">
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'following' ? (
            // Following Tab
            followedStreams.length === 0 ? (
              <div className="text-center py-20">
                <Heart className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Followed Streams Live</h3>
                <p className="text-white/60 mb-6">Follow streamers to see when they go live!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {followedStreams.map((stream) => {
                  const category = STREAM_CATEGORIES.find(c => c.id === stream.category);
                  return (
                    <motion.div
                      key={stream.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl overflow-hidden bg-white/5 border border-pink-500/30 hover:border-pink-500/50 transition-colors cursor-pointer group"
                      onClick={() => handleJoinFromDiscover(stream)}
                    >
                      <div className="relative aspect-video" style={{ background: `linear-gradient(135deg, ${category?.color || '#8B5CF6'}40, ${category?.color || '#8B5CF6'}20)` }}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-5xl">{category?.icon || '📺'}</span>
                        </div>
                        <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 rounded-full bg-red-500 text-white text-xs font-semibold">
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          LIVE
                        </div>
                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-pink-500 text-white text-xs">
                          <Heart className="w-3 h-3 fill-current" />
                          Following
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white mb-1 truncate">{stream.title}</h3>
                        <p className="text-white/60 text-sm">{stream.streamer_name}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )
          ) : activeTab === 'scheduled' ? (
            // Upcoming Scheduled Streams Tab
            upcomingStreams.length === 0 ? (
              <div className="text-center py-20">
                <svg className="w-16 h-16 text-white/20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-semibold text-white mb-2">No Upcoming Streams</h3>
                <p className="text-white/60">Check back later for scheduled streams!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingStreams.map((stream) => {
                  const category = STREAM_CATEGORIES.find(c => c.id === stream.category);
                  const scheduledDate = new Date(stream.scheduled_time);
                  return (
                    <div key={stream.id} className="rounded-2xl overflow-hidden bg-white/5 border border-amber-500/30">
                      <div className="relative aspect-video" style={{ background: `linear-gradient(135deg, ${category?.color || '#8B5CF6'}40, ${category?.color || '#8B5CF6'}20)` }}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-5xl">{category?.icon || '📺'}</span>
                        </div>
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold">
                          SCHEDULED
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white mb-1 truncate">{stream.title}</h3>
                        <p className="text-white/60 text-sm mb-2">{stream.streamer_name}</p>
                        <div className="flex items-center gap-2 text-amber-400 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {scheduledDate.toLocaleDateString()} at {scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // All Live Streams Tab
            loadingStreams ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent" />
            </div>
          ) : liveStreams.length === 0 ? (
            <div className="text-center py-20">
              <Radio className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Live Streams</h3>
              <p className="text-white/60 mb-6">Be the first to go live!</p>
              <button
                onClick={() => setMode(null)}
                className="px-6 py-3 rounded-xl font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #EC4899 100%)' }}
              >
                Start Your Stream
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveStreams.map((stream) => {
                const category = STREAM_CATEGORIES.find(c => c.id === stream.category);
                return (
                <motion.div
                  key={stream.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-colors cursor-pointer group"
                  onClick={() => handleJoinFromDiscover(stream)}
                  data-testid={`stream-card-${stream.id}`}
                >
                  {/* Stream Preview */}
                  <div className="relative aspect-video" style={{ background: `linear-gradient(135deg, ${category?.color || '#8B5CF6'}40, ${category?.color || '#8B5CF6'}20)` }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-5xl">{category?.icon || '📺'}</span>
                    </div>
                    
                    {/* Live Badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 rounded-full bg-red-500 text-white text-xs font-semibold">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      LIVE
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute bottom-3 left-3 px-2 py-1 rounded-full text-white text-xs font-medium" style={{ background: category?.color || '#8B5CF6' }}>
                      {category?.name || 'Unknown'}
                    </div>
                    
                    {/* Viewer Count */}
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 text-white text-xs">
                      <Eye className="w-3 h-3" />
                      {stream.viewer_count || 0}
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="px-4 py-2 rounded-full bg-white text-black font-semibold text-sm">
                        Join Stream
                      </div>
                    </div>
                  </div>

                  {/* Stream Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-1 truncate">{stream.title || 'Untitled Stream'}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                          {stream.streamer_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <span>{stream.streamer_name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white/40 text-xs">
                        {LANGUAGES.find(l => l.code === stream.language)?.flag || '🌐'}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )})}
            </div>
          )
          )}
        </div>
      </motion.div>
    );
  }

  // Schedule Stream Screen
  if (mode === 'schedule') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
        data-testid="schedule-streams-container"
      >
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMode(null)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-white">Schedule Streams</h2>
                <p className="text-white/60 text-sm">Plan your future broadcasts</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Create Schedule Form */}
            <div className="p-4 sm:p-6 rounded-2xl bg-white/5 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Schedule New Stream</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Title</label>
                  <input
                    data-testid="schedule-title-input"
                    type="text"
                    value={scheduleTitle}
                    onChange={(e) => setScheduleTitle(e.target.value)}
                    placeholder="Stream title..."
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-amber-400"
                  />
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-1 block">Category</label>
                  <select
                    data-testid="schedule-category-select"
                    value={scheduleCategory}
                    onChange={(e) => setScheduleCategory(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-amber-400"
                  >
                    {STREAM_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id} className="bg-gray-900">
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Date</label>
                    <input
                      data-testid="schedule-date-input"
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Time</label>
                    <input
                      data-testid="schedule-time-input"
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-1 block">Description (optional)</label>
                  <textarea
                    data-testid="schedule-description-input"
                    value={scheduleDescription}
                    onChange={(e) => setScheduleDescription(e.target.value)}
                    placeholder="What will you be streaming?"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-amber-400 resize-none"
                    rows={3}
                  />
                </div>

                <button
                  data-testid="create-schedule-button"
                  onClick={handleScheduleStream}
                  className="w-full py-3 rounded-xl font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)' }}
                >
                  Schedule Stream
                </button>
              </div>
            </div>

            {/* My Scheduled Streams */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Your Scheduled Streams</h3>
              
              {scheduledStreams.length === 0 ? (
                <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
                  <svg className="w-12 h-12 text-white/20 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-white/60">No scheduled streams yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledStreams.map((stream) => (
                    <div key={stream.id} className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-white">{stream.title}</h4>
                          <p className="text-sm text-white/60">
                            {STREAM_CATEGORIES.find(c => c.id === stream.category)?.icon} {STREAM_CATEGORIES.find(c => c.id === stream.category)?.name}
                          </p>
                          <p className="text-sm text-amber-400 mt-1">
                            {new Date(stream.scheduled_time).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCancelScheduledStream(stream.id)}
                          className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Earnings Dashboard Screen
  if (mode === 'earnings') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
        data-testid="earnings-container"
      >
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMode(null)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-white">Earnings Dashboard</h2>
                <p className="text-white/60 text-sm">Track your streaming revenue</p>
              </div>
            </div>
          </div>

          {earnings ? (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-white/10">
                  <p className="text-white/60 text-xs sm:text-sm mb-1">Total Earnings</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-400">${earnings.total_earnings?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10">
                  <p className="text-white/60 text-xs sm:text-sm mb-1">Today</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">${earnings.today?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10">
                  <p className="text-white/60 text-xs sm:text-sm mb-1">This Week</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">${earnings.this_week?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10">
                  <p className="text-white/60 text-xs sm:text-sm mb-1">This Month</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">${earnings.this_month?.toFixed(2) || '0.00'}</p>
                </div>
              </div>

              {/* Recent Tips */}
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Tips</h3>
                
                {earnings.recent_tips?.length === 0 ? (
                  <div className="text-center py-8">
                    <Gift className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60">No tips received yet</p>
                    <p className="text-white/40 text-sm">Start streaming to receive tips from your viewers!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {earnings.recent_tips?.map((tip, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                            {tip.sender_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-white">{tip.sender_name}</p>
                            {tip.message && <p className="text-sm text-white/60">"{tip.message}"</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-400">${tip.amount?.toFixed(2)}</p>
                          <p className="text-xs text-white/40">
                            {new Date(tip.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent" />
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Streaming/Viewing Interface
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 bg-black z-50 flex ${isFullscreen ? '' : 'p-4'}`}
      data-testid="livestream-container"
    >
      <div className={`flex-1 flex ${isFullscreen ? '' : 'gap-4'}`}>
        {/* Main Video Area */}
        <div className={`relative flex-1 ${isFullscreen ? '' : 'rounded-3xl overflow-hidden'}`}>
          {/* Video */}
          <div className="absolute inset-0 bg-gray-900">
            {mode === 'streamer' ? (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            ) : (
              remoteStream ? (
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted={isMuted}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-4" />
                    <p className="text-white/70">Connecting to stream...</p>
                  </div>
                </div>
              )
            )}

            {/* Video disabled overlay */}
            {mode === 'streamer' && !isVideoEnabled && (
              <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                <VideoOff className="w-16 h-16 text-white/50" />
              </div>
            )}
          </div>

          {/* Floating Reactions */}
          <AnimatePresence>
            {reactions.map(reaction => (
              <motion.div
                key={reaction.id}
                initial={{ y: 0, opacity: 1, scale: 1 }}
                animate={{ y: -400, opacity: 0, scale: 1.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 3, ease: 'easeOut' }}
                className="absolute bottom-32 text-4xl pointer-events-none"
                style={{ left: `${reaction.x}%` }}
              >
                {reaction.emoji}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isLive && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500 text-white text-sm font-semibold">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    LIVE
                  </div>
                )}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm">
                  <Eye className="w-4 h-4" />
                  {viewerCount} watching
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Language Selection */}
                <button
                  data-testid="stream-language-button"
                  onClick={() => setShowLanguageSelect(!showLanguageSelect)}
                  className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
                >
                  <Languages className="w-5 h-5" />
                </button>

                {/* Fullscreen Toggle */}
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
                >
                  {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>

                {/* Close */}
                <button
                  data-testid="close-stream-button"
                  onClick={handleLeaveStream}
                  className="p-2 rounded-full bg-white/20 text-white hover:bg-red-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Language Selector Dropdown */}
            <AnimatePresence>
              {showLanguageSelect && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-16 right-4 bg-gray-900 rounded-2xl shadow-2xl p-4 max-h-80 overflow-y-auto z-10"
                >
                  <p className="text-sm font-semibold text-white mb-3">Translate to:</p>
                  <div className="space-y-1">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setSelectedLanguage(lang.code);
                          setShowLanguageSelect(false);
                        }}
                        className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2 ${
                          selectedLanguage === lang.code
                            ? 'bg-purple-600 text-white'
                            : 'text-white/70 hover:bg-white/10'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Stream Info */}
          <div className="absolute bottom-24 left-4 right-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center text-white font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white font-semibold">{streamTitle || 'Live Stream'}</p>
                <p className="text-white/60 text-sm">{user.name}</p>
              </div>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-center gap-3">
              {mode === 'streamer' ? (
                <>
                  {/* Streamer Controls */}
                  <button
                    data-testid="toggle-stream-video"
                    onClick={toggleVideo}
                    className={`p-4 rounded-full ${isVideoEnabled ? 'bg-white/20' : 'bg-red-500'}`}
                  >
                    {isVideoEnabled ? (
                      <Video className="w-6 h-6 text-white" />
                    ) : (
                      <VideoOff className="w-6 h-6 text-white" />
                    )}
                  </button>

                  <button
                    data-testid="toggle-stream-audio"
                    onClick={toggleAudio}
                    className={`p-4 rounded-full ${isAudioEnabled ? 'bg-white/20' : 'bg-red-500'}`}
                  >
                    {isAudioEnabled ? (
                      <Mic className="w-6 h-6 text-white" />
                    ) : (
                      <MicOff className="w-6 h-6 text-white" />
                    )}
                  </button>

                  <button
                    data-testid="end-stream-button"
                    onClick={handleLeaveStream}
                    className="px-6 py-4 rounded-full bg-red-500 text-white font-semibold flex items-center gap-2"
                  >
                    End Stream
                  </button>
                </>
              ) : (
                <>
                  {/* Viewer Controls */}
                  <button
                    data-testid="toggle-mute"
                    onClick={() => setIsMuted(!isMuted)}
                    className={`p-4 rounded-full ${isMuted ? 'bg-red-500' : 'bg-white/20'}`}
                  >
                    {isMuted ? (
                      <VolumeX className="w-6 h-6 text-white" />
                    ) : (
                      <Volume2 className="w-6 h-6 text-white" />
                    )}
                  </button>

                  {/* Quick Reactions */}
                  {['❤️', '🔥', '👏', '😂', '😮'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => sendReaction(emoji)}
                      className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-2xl"
                    >
                      {emoji}
                    </button>
                  ))}

                  <button
                    data-testid="send-gift-button"
                    onClick={() => setShowGiftModal(true)}
                    className="p-4 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500"
                  >
                    <Gift className="w-6 h-6 text-white" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Chat Panel */}
        {!isFullscreen && (
          <div className="w-80 bg-gray-900 rounded-3xl flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white">Live Chat</span>
                </div>
                <button
                  onClick={() => setTranslationEnabled(!translationEnabled)}
                  className={`p-2 rounded-lg ${translationEnabled ? 'bg-purple-600' : 'bg-white/10'}`}
                  title={translationEnabled ? 'Translation ON' : 'Translation OFF'}
                >
                  <Languages className="w-4 h-4 text-white" />
                </button>
              </div>
              {translationEnabled && (
                <p className="text-xs text-white/50 mt-2">
                  Translating to {LANGUAGES.find(l => l.code === selectedLanguage)?.name}
                </p>
              )}
            </div>

            {/* Chat Messages */}
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-3"
              data-testid="stream-chat-messages"
            >
              {chatMessages.map((msg, index) => (
                <div key={index} className="animate-fadeIn">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {msg.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/80 text-sm">
                        <span className="font-semibold text-purple-400">{msg.user_name}</span>
                        {msg.original_language !== selectedLanguage && (
                          <span className="text-xs text-white/40 ml-1">
                            ({LANGUAGES.find(l => l.code === msg.original_language)?.flag})
                          </span>
                        )}
                      </p>
                      <p className="text-white text-sm break-words">
                        {msg.translated_message || msg.message}
                      </p>
                      {msg.translated_message && msg.message !== msg.translated_message && (
                        <p className="text-white/40 text-xs mt-1 italic">
                          Original: {msg.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <form onSubmit={sendChatMessage} className="p-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    data-testid="stream-chat-input"
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message in ${LANGUAGES.find(l => l.code === selectedLanguage)?.name}...`}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 outline-none focus:border-purple-500"
                  />
                </div>
                <button
                  data-testid="send-chat-message"
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="p-3 rounded-xl bg-purple-600 text-white disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Gift Panel */}
      <GiftPanel
        isOpen={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        onSendGift={handleSendGift}
        onPremiumGift={handlePremiumGift}
        userDiamonds={userDiamonds}
        senderInfo={{ id: user?.id, name: user?.name, avatar: user?.avatar }}
      />

      {/* Premium Gift Effects (Full-screen Castle/Planet animations) */}
      <PremiumGiftEffects />

      {/* Live Stream Stats Overlay */}
      {mode === 'streamer' && isLive && (
        <LiveStreamStats
          diamonds={giftSystem.totalDiamonds}
          viewers={viewerCount}
          duration={streamInfo?.duration || '0:00'}
          topGifters={giftSystem.topGifters}
        />
      )}
    </motion.div>
  );
};

export default LiveStream;
